"use client"

import { useEffect, useMemo, useRef } from "react"
import { EditorContent } from "@tiptap/react"
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Link2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Undo,
  Redo,
} from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Tabs, TabsList, TabsTab } from "@/components/ui/tabs"
import { useContractEditor, getContractEditorInitialContent } from "./use-contract-editor"
import { cn } from "@/lib/utils"
import { getContractViewerContent } from "@/components/contract-viewer/utils"
import type { ContractVariable, GetContractResponseContent } from "src/types/contract.types"

function ToolbarSeparator() {
  return <div className="w-px h-5 bg-border mx-1" />
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  children,
  title,
}: {
  onClick?: () => void
  active?: boolean
  disabled?: boolean
  children: React.ReactNode
  title?: string
}) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
      title={title}
      className={cn(
        "size-8 text-muted-foreground hover:text-foreground hover:bg-accent/50 disabled:opacity-40 disabled:cursor-not-allowed",
        active && "bg-accent text-foreground"
      )}
    >
      {children}
    </Button>
  )
}

type ContractEditorMode = "edit" | "viewer"

interface ContractEditorProps {
  mode?: ContractEditorMode
  onModeChange?: (mode: ContractEditorMode) => void
  contract?: GetContractResponseContent | null
  highlightedTerm?: string | null
  onHighlightedTermSelect?: (termWithInstance: string) => void
  taggedContractText?: string
  onTaggedContractTextChange?: (text: string) => void
}

const EDITOR_CONTENT_CLASSES =
  "[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[800px] [&_.ProseMirror]:text-slate-700 [&_.ProseMirror_p]:mb-4 [&_.ProseMirror_p]:leading-relaxed [&_.ProseMirror_p:last-child]:mb-0 [&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-semibold [&_.ProseMirror_h1]:mb-4 [&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:mb-3 [&_.ProseMirror_h3]:text-lg [&_.ProseMirror_h3]:font-medium [&_.ProseMirror_h3]:mb-2"

const REGEX_ESCAPE_PATTERN = /[.*+?^${}()|[\]\\]/g

const escapeRegex = (value: string) => value.replace(REGEX_ESCAPE_PATTERN, "\\$&")

type VariableMatch = {
  start: number
  end: number
  value: string
  variable: ContractVariable
}

const buildTaggedContractText = (
  plainText: string,
  contract?: GetContractResponseContent | null
): string => {
  const normalizedText = plainText.replace(/\r\n/g, "\n").trim()
  if (!normalizedText) return ""

  const variables = Object.values(contract?.contractExtraction?.variables ?? {})
    .filter((variable): variable is ContractVariable => Boolean(variable?.id && variable?.name?.trim()))
    .sort((a, b) => b.name.length - a.name.length)

  if (variables.length === 0) {
    return normalizedText
  }

  const matches: VariableMatch[] = []

  variables.forEach((variable) => {
    const candidateTerms = new Set<string>()
    const variableName = variable.name.trim()
    if (variableName.length > 0) candidateTerms.add(variableName)

    variable.variations?.forEach((variation) => {
      const normalizedVariation = variation.trim()
      if (normalizedVariation.length > 1) {
        candidateTerms.add(normalizedVariation)
      }
    })

    const shortValue = variable.value?.trim()
    if (shortValue && shortValue.length <= 24) {
      candidateTerms.add(shortValue)
    }

    candidateTerms.forEach((term) => {
      const boundaryRegex = new RegExp(`\\b${escapeRegex(term)}\\b`, "gi")
      let match: RegExpExecArray | null
      while ((match = boundaryRegex.exec(normalizedText)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          value: match[0],
          variable,
        })
      }
    })
  })

  if (matches.length === 0) {
    return normalizedText
  }

  matches.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start))

  const selectedMatches: VariableMatch[] = []
  let selectedCursor = 0

  matches.forEach((match) => {
    if (match.start < selectedCursor) return
    selectedMatches.push(match)
    selectedCursor = match.end
  })

  const instanceByTermId: Record<string, number> = {}
  let output = ""
  let sourceCursor = 0

  selectedMatches.forEach((match) => {
    output += normalizedText.slice(sourceCursor, match.start)

    const currentInstance = (instanceByTermId[match.variable.id] || 0) + 1
    instanceByTermId[match.variable.id] = currentInstance

    const attributes = [
      `type="${match.variable.type}"`,
      `id="${match.variable.id}"`,
      `instance="${currentInstance}"`,
    ]

    if (typeof match.variable.level === "number") {
      attributes.push(`level="${match.variable.level}"`)
    }

    output += `<variable ${attributes.join(" ")}>${match.value}</variable>`
    sourceCursor = match.end
  })

  output += normalizedText.slice(sourceCursor)
  return output
}

export function ContractEditor({
  mode = "edit",
  onModeChange,
  contract,
  highlightedTerm = null,
  onHighlightedTermSelect,
  taggedContractText,
  onTaggedContractTextChange,
}: ContractEditorProps) {
  const documentAreaRef = useRef<HTMLDivElement>(null)
  const taggedText = contract?.contractExtraction?.contractTexts?.taggedText?.text
  const originalText = contract?.contractExtraction?.contractTexts?.originalText?.text
  const initialContent = useMemo(
    () => getContractEditorInitialContent(contract),
    [contract?.contractId, taggedText, originalText]
  )
  const handleEditorTextChange = useMemo(
    () =>
      onTaggedContractTextChange
        ? (text: string) => {
            onTaggedContractTextChange(buildTaggedContractText(text, contract))
          }
        : undefined,
    [contract, onTaggedContractTextChange]
  )

  const { editor, toggleBold, toggleItalic, toggleUnderline } = useContractEditor({
    initialContent,
    editable: mode === "edit",
    onTextChange: mode === "edit" ? handleEditorTextChange : undefined,
  })

  useEffect(() => {
    if (mode !== "viewer" || !highlightedTerm) return

    const documentArea = documentAreaRef.current
    if (!documentArea) return

    const [termId, instance] = highlightedTerm.split(":")
    const termSelector = instance
      ? `[data-term-id="${termId}"][data-instance="${instance}"]`
      : `[data-term-id="${termId}"]`

    const matchedTerm =
      documentArea.querySelector<HTMLElement>(termSelector) ??
      (instance ? documentArea.querySelector<HTMLElement>(`[data-term-id="${termId}"]`) : null)

    if (!matchedTerm) return

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches

    matchedTerm.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "center",
      inline: "nearest",
    })
  }, [highlightedTerm, mode])

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b bg-background sticky top-0 z-10">
        <div className="flex items-center gap-0.5">
          <ToolbarButton
            onClick={toggleBold}
            active={editor?.isActive("bold")}
            disabled={mode !== "edit"}
            title="Bold"
          >
            <Bold className="size-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleItalic}
            active={editor?.isActive("italic")}
            disabled={mode !== "edit"}
            title="Italic"
          >
            <Italic className="size-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleUnderline}
            active={editor?.isActive("underline")}
            disabled={mode !== "edit"}
            title="Underline"
          >
            <UnderlineIcon className="size-4" />
          </ToolbarButton>
          <ToolbarButton disabled={mode !== "edit"} title="Strikethrough">
            <Strikethrough className="size-4" />
          </ToolbarButton>
          <ToolbarButton disabled={mode !== "edit"} title="Link">
            <Link2 className="size-4" />
          </ToolbarButton>

          <ToolbarSeparator />

          <ToolbarButton disabled={mode !== "edit"} title="Align left">
            <AlignLeft className="size-4" />
          </ToolbarButton>
          <ToolbarButton disabled={mode !== "edit"} title="Align center">
            <AlignCenter className="size-4" />
          </ToolbarButton>
          <ToolbarButton disabled={mode !== "edit"} title="Align right">
            <AlignRight className="size-4" />
          </ToolbarButton>
          <ToolbarButton disabled={mode !== "edit"} title="Justify">
            <AlignJustify className="size-4" />
          </ToolbarButton>

          <ToolbarSeparator />

          <ToolbarButton disabled={mode !== "edit"} title="Bullet list">
            <List className="size-4" />
          </ToolbarButton>
          <ToolbarButton disabled={mode !== "edit"} title="Numbered list">
            <ListOrdered className="size-4" />
          </ToolbarButton>

          <ToolbarSeparator />

          <ToolbarButton
            onClick={() => editor?.chain().focus().undo().run()}
            disabled={mode !== "edit"}
            title="Undo"
          >
            <Undo className="size-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor?.chain().focus().redo().run()}
            disabled={mode !== "edit"}
            title="Redo"
          >
            <Redo className="size-4" />
          </ToolbarButton>
        </div>

        <Tabs
          value={mode}
          onValueChange={(value) => onModeChange?.(value as ContractEditorMode)}
        >
          <TabsList>
            <TabsTab value="edit">Edit Mode</TabsTab>
            <TabsTab value="viewer">View Mode</TabsTab>
          </TabsList>
        </Tabs>
      </div>

      <div ref={documentAreaRef} className="flex-1 overflow-auto bg-muted/40">
        <div className="min-h-full py-8 px-12">
          <div className="max-w-[850px] mx-auto bg-white text-black shadow-lg rounded-sm border">
            <div className="px-16 py-12 min-h-[1100px]">
              {mode === "edit" ? (
                <EditorContent
                  editor={editor}
                  className={EDITOR_CONTENT_CLASSES}
                />
              ) : (
                getContractViewerContent(
                  highlightedTerm,
                  contract ?? undefined,
                  onHighlightedTermSelect,
                  {
                    theme: "light",
                    contractTextOverride: taggedContractText,
                  }
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
